import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";

import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerForm from "ds/components/DrawerNew/Form";
import Banner from "ds/components/Banner";
import AssignRoleForm from "components/AssignRoleForm";
import { AssignRoleFormAddFields, AssignRoleFormEditFields } from "components/AssignRoleForm/types";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import { ROOT_SPACE_ID } from "constants/space";
import Box from "ds/components/Box";
import { pluralize } from "shared/Pluralize";

import {
  API_KEY_ROLE_BINDINGS_WITH_ROLES,
  API_KEY_ROLE_BINDING_UPDATE,
  API_KEY_ROLE_BINDING_BATCH_CREATE,
} from "./gql";
import { showUnassignConfirmation } from "../UnassignConfirmation";
import { UserManagementActivationStatus } from "../../types";
import { SPACE_ADMIN_ROLE_SLUG } from "../../constants";

type ManageRolesDrawerProps = {
  apiKeyId: string;
  activationStatus: UserManagementActivationStatus;
};

const SELECTABLE_SPACE_IDS_FOR_LOGIN_POLICY = [ROOT_SPACE_ID];

const refetchQueries = ["ApiKeyRoleBindingsWithRoles"];

const ManageRolesDrawer = createDrawer(({ apiKeyId, activationStatus }: ManageRolesDrawerProps) => {
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const setDirtyFieldsHandler = useCallback(
    (formId: string, value: boolean) => setDirtyFields((prev) => ({ ...prev, [formId]: value })),
    []
  );

  const { data, loading: rolesLoading } = useQuery(API_KEY_ROLE_BINDINGS_WITH_ROLES, {
    variables: { apiKeyId },
  });

  const [createBatchRoleBinding, { loading: createBatchRoleBindingLoading }] = useMutation(
    API_KEY_ROLE_BINDING_BATCH_CREATE,
    {
      refetchQueries,
      awaitRefetchQueries: true,
    }
  );
  const [updateRoleBinding] = useMutation(API_KEY_ROLE_BINDING_UPDATE, {
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const isLoginPolicyActive = useMemo(
    () => activationStatus === UserManagementActivationStatus.INACTIVE,
    [activationStatus]
  );

  const allRoles = useMemo(() => data?.roles || [], [data?.roles]);

  const loginPolicySelectableRoleIds = useMemo(() => {
    return allRoles.filter((role) => role.slug === SPACE_ADMIN_ROLE_SLUG).map((role) => role.id);
  }, [allRoles]);

  const currentApiKey = data?.apiKey;

  const assignedRoles = useMemo(() => {
    return (
      currentApiKey?.apiKeyRoleBindings.map((binding) => ({
        id: binding.id,
        roleID: binding.role.id,
        spaceID: binding.space.id,
        spaceName: binding.space.name,
      })) || []
    );
  }, [currentApiKey?.apiKeyRoleBindings]);

  const handleCreate = useCallback(
    (formData: AssignRoleFormAddFields) => {
      const roleBindings = formData.roleIds.map((roleId) => ({
        apiKeyID: apiKeyId,
        roleID: roleId,
        spaceID: formData.space,
      }));

      return createBatchRoleBinding({
        variables: {
          input: {
            bindings: roleBindings,
          },
        },
      })
        .then(() => {
          reportSuccess({
            message: `${pluralize("Role", roleBindings.length)} successfully assigned`,
          });
        })
        .catch(onError);
    },
    [apiKeyId, createBatchRoleBinding, onError, reportSuccess]
  );

  const handleEdit = useCallback(
    (formData: AssignRoleFormEditFields, roleAssignmentId: string) => {
      updateRoleBinding({
        variables: {
          id: roleAssignmentId,
          input: {
            apiKeyID: apiKeyId,
            roleID: formData.roleId,
            spaceID: formData.space,
          },
        },
      })
        .then(() => {
          reportSuccess({ message: "Role successfully updated" });
        })
        .catch(onError);
    },
    [apiKeyId, updateRoleBinding, onError, reportSuccess]
  );

  const handleDelete = (roleAssignmentId: string, roleName: string) => {
    const isLastRole = assignedRoles.length === 1;
    showUnassignConfirmation({
      id: roleAssignmentId,
      name: roleName,
      refetchQueries,
      isLastRole,
      activationStatus,
    });
  };

  const isDirty = useMemo(() => !!Object.values(dirtyFields).filter(Boolean).length, [dirtyFields]);

  return (
    <DrawerForm isDirty={isDirty}>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle title="Manage roles" />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody>
        <Box direction="column" gap="x-large">
          {isLoginPolicyActive && (
            <Banner variant="warning">
              Roles can't be assigned to API keys from the UI if they're governed by a login policy.
              Instead, attach roles within the login policy itself.{" "}
              <strong>The only exception is the root admin API key</strong>, which bypasses all
              access controls and should be used only as a last resort.
            </Banner>
          )}
          <AssignRoleForm
            onCreate={handleCreate}
            onEdit={isLoginPolicyActive ? undefined : handleEdit}
            onDelete={handleDelete}
            setDirtyFields={setDirtyFieldsHandler}
            availableRoles={allRoles}
            assignedRoles={assignedRoles}
            loading={rolesLoading}
            createLoading={createBatchRoleBindingLoading}
            selectableRoleIds={isLoginPolicyActive ? loginPolicySelectableRoleIds : undefined}
            selectableSpaceIds={
              isLoginPolicyActive ? SELECTABLE_SPACE_IDS_FOR_LOGIN_POLICY : undefined
            }
          />
        </Box>
      </DrawerBody>
    </DrawerForm>
  );
});

export const showManageRolesDrawer = createDrawerTrigger(ManageRolesDrawer);
